import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { toggleCollection } from "../../../redux/collectionSlice";
import { getHistoryCycleContainer } from "../../../services/container";
import axios from "axios";
import cloneDeep from "lodash/cloneDeep";
import { starsCalculation } from "../ActivityUtilities";
/**
 * Hook pour gérer le chargement des données d'une ligne de collection
 * @param {{ enabled?: boolean }} options - when enabled is false, skip automatic fetch on mount
 */
export const useCollectionRowData = (
  timeLine,
  superContainerTimeLine,
  options = {},
) => {
  const { enabled = true } = options;
  const [loading, setLoading] = useState(true);
  const [historyCycle, setHistoryCycle] = useState([]);
  const [numberOfPackmat, setNumberOfPackmat] = useState(0);
  const [numberOfStars, setNumberOfStars] = useState(3);
  const [numberOfAnomaly, setNumberOfAnomaly] = useState(0);
  const [totalVolumeSortingError, setTotalVolumeSortingError] = useState(0);
  const [totalWasteVolume, setTotalWasteVolume] = useState(0);
  const [totalMassSortingError, setTotalMassSortingError] = useState(0);
  const [totalWasteMass, setTotalWasteMass] = useState(0);
  const [collectionFillingLevel, setCollectionFillingLevel] = useState(0);
  const [dropOff, setDropOff] = useState("");
  const [imageData, setImageData] = useState(null);
  const [reviewComments, setReviewComments] = useState([]);
  const dispatch = useDispatch();

  const processHistoryCycle = async (item, isMainContainer = false) => {
    const response = await getHistoryCycleContainer(
      {
        currentCycle: false,
        getLevelLastRotation: false,
        containerCollectionDate: item._time,
      },
      item._measurement,
    );

    const [resHistoryCycle] = await axios.all([response]);
    if (resHistoryCycle.status !== 200) return null;

    const {
      historyCycle,
      numberOfAnomaly,
      numberOfStars,
      anomalyDelivery,
      numberOfPackmat,
      totalVolumeSortingError,
      totalWasteVolume,
      totalMassSortingError,
      totalWasteMass,
      lastPackmatEfficiency,
      objectDelivery,
      numberOfObjects,
      reviewComments: reviewCommentsFromApi,
    } = resHistoryCycle.data;

    const historyData = {
      historyCurrent: historyCycle,
      numberOfAnomaly,
      numberOfStars,
      anomalyDelivery,
      numberOfPackmat,
      totalVolumeSortingError,
      totalWasteVolume,
      totalMassSortingError,
      totalWasteMass,
      collectionFillingLevel: historyCycle[0].filling_level,
      lastPackmatEfficiency: lastPackmatEfficiency?.[0] || null,
      objectDelivery,
      numberOfObjects,
      reviewComments: reviewCommentsFromApi || [],
    };

    if (isMainContainer) {
      if (reviewCommentsFromApi && reviewCommentsFromApi.length > 0) {
        setReviewComments(reviewCommentsFromApi);
      }
      setHistoryCycle(historyCycle);
      setCollectionFillingLevel(historyCycle[0].filling_level);
      setDropOff(historyCycle[historyCycle.length - 1]?._time);
      setImageData(
        cloneDeep(
          historyCycle[timeLine.showDepotCycle ? historyCycle.length - 1 : 0],
        ),
      );
      setNumberOfPackmat(numberOfPackmat);
    }

    return historyData;
  };

  const fetchData = async () => {
    setLoading(true);

    try {
      if (superContainerTimeLine && superContainerTimeLine.length > 0) {
        const superContainerHistoryData = [];

        for (let i = 0; i < superContainerTimeLine.length; i++) {
          const item = superContainerTimeLine[i];
          const historyData = await processHistoryCycle(item, i === 0);
          if (historyData) superContainerHistoryData.push(historyData);
        }

        const mergedVolumeSortingError = superContainerHistoryData.reduce(
          (acc, item) => acc + (item.totalVolumeSortingError || 0),
          0,
        );
        const mergedNumberOfAnomaly = superContainerHistoryData.reduce(
          (acc, item) => acc + (item.numberOfAnomaly || 0),
          0,
        );
        const mergedTotalWasteVolume = superContainerHistoryData.reduce(
          (acc, item) => acc + (item.totalWasteVolume || 0),
          0,
        );
        const mergedReviewComments = superContainerHistoryData.reduce(
          (acc, item) => acc.concat(item.reviewComments || []),
          [],
        );

        const stars = starsCalculation(
          mergedVolumeSortingError,
          mergedTotalWasteVolume,
        );

        const mergedMassSortingError = superContainerHistoryData.reduce(
          (acc, item) => acc + (item.totalMassSortingError || 0),
          0,
        );
        const mergedTotalWasteMass = superContainerHistoryData.reduce(
          (acc, item) => acc + (item.totalWasteMass || 0),
          0,
        );

        setNumberOfAnomaly(mergedNumberOfAnomaly);
        setTotalVolumeSortingError(mergedVolumeSortingError);
        setTotalWasteVolume(mergedTotalWasteVolume);
        setTotalMassSortingError(mergedMassSortingError);
        setTotalWasteMass(mergedTotalWasteMass);
        setNumberOfStars(stars);
        setReviewComments(mergedReviewComments);

        dispatch(
          toggleCollection({
            [`${timeLine._measurement}_${timeLine._time}`]:
              superContainerHistoryData,
          }),
        );
      } else {
        const historyData = await processHistoryCycle(timeLine, true);
        if (historyData) {
          setNumberOfAnomaly(historyData.numberOfAnomaly);
          setTotalVolumeSortingError(historyData.totalVolumeSortingError);
          setTotalWasteVolume(historyData.totalWasteVolume);
          setTotalMassSortingError(historyData.totalMassSortingError);
          setTotalWasteMass(historyData.totalWasteMass);
          setNumberOfStars(historyData.numberOfStars);
          if (
            historyData.reviewComments &&
            historyData.reviewComments.length > 0
          ) {
            setReviewComments(historyData.reviewComments);
          }

          dispatch(
            toggleCollection({
              [`${timeLine._measurement}_${timeLine._time}`]: historyData,
            }),
          );
        }
      }
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(
    () => {
      if (!enabled || !timeLine?._measurement || !timeLine?._time) return;
      fetchData();
    },
    // eslint-disable-next-line
    [enabled, timeLine?._measurement, timeLine?._time, superContainerTimeLine],
  );

  return {
    loading,
    historyCycle,
    numberOfPackmat,
    numberOfStars,
    numberOfAnomaly,
    totalVolumeSortingError,
    totalWasteVolume,
    totalMassSortingError,
    totalWasteMass,
    collectionFillingLevel,
    dropOff,
    imageData,
    reviewComments,
    refetch: fetchData,
  };
};
